// Legătura cu terminalul Tiger.Trade, prin serverul lui local.
//
// Tiger.com Windows pornește un server WebSocket pe `127.0.0.1:7819`, activat din
// `Settings → Local Signal Server → Enable Signal Server`. Comanda `setLinkSymbol`
// deschide instant o pereche în grupul de legătură ales (A–11), adică exact ce ne
// trebuie: apeși iconița lângă monedă, moneda se deschide în terminal.
//
// Conexiunea e leneșă: nu deschidem socketul până nu apeși prima dată. Cei mai mulți
// nu au terminalul pornit, iar o încercare de conectare la fiecare încărcare de pagină
// ar umple consola cu erori degeaba.

import { bus } from './util.js';
import { settings } from './state.js';

const PORT_IMPLICIT = 7819;
const KEY = 'sx_tiger';

/**
 * Numele burselor așa cum le știe terminalul. În documentația lor apar în două forme
 * („BINANCE", dar și „TigerX Binance"), iar tabelul complet nu e public — de aceea
 * fiecare se poate corecta din setări, fără să atingem codul.
 */
const NUME_IMPLICITE = {
  // Măsurat pe un terminal real: numele diferă **după piață**, nu doar după bursă.
  // Perpetuele Binance prin Tiger.com Broker sunt „BINANCE-FUT" (instrumentele
  // `…_FUT_BINANCE-FUT`, pe care stă contul brokerului). „TigerX Binance" e alt conector:
  // terminalul îl acceptă, dar fără contul brokerului pe el. Numele greșit primește
  // „Symbol not found" sau deschide un instrument pe care nu poți tranzacționa.
  binance: { futures: 'BINANCE-FUT', spot: 'BINANCE' },
  bybit: { futures: 'BYBIT', spot: 'BYBIT' },
  okx: { futures: 'TigerX OKX', spot: 'OKX' },
  bitget: { futures: 'BITGET', spot: 'BITGET' },
  gate: { futures: 'GATE', spot: 'GATE' },
  mexc: { futures: 'MEXC', spot: 'MEXC' },
};

/** Numele pentru o pereche bursă+piață, cu alegerea utilizatorului deasupra. */
function numeBursa(exchange, market) {
  const st = tigerState();
  const ales = st.names?.[exchange];
  const piata = market === 'spot' ? 'spot' : 'futures';
  // Forma veche (un singur nume per bursă) era o presupunere nverificată; acum avem
  // harta măsurată pe terminal, deci o ignorăm în loc s-o lăsăm să umbrească defaultul.
  if (ales && typeof ales === 'object' && ales[piata]) return ales[piata];
  return NUME_IMPLICITE[exchange]?.[piata] || exchange.toUpperCase();
}

export function tigerState() {
  const salvat = settings.get(KEY, null) || {};
  // Curățăm forma veche o singură dată, la citire.
  const names = { ...NUME_IMPLICITE };
  for (const [ex, v] of Object.entries(salvat.names || {})) {
    if (v && typeof v === 'object') names[ex] = { ...NUME_IMPLICITE[ex], ...v };
  }
  return {
    // Pornită implicit: iconița nu strică nimic dacă terminalul nu rulează — clicul
    // spune atunci ce lipsește. Ascunsă implicit, în schimb, nu o găsește nimeni.
    on: true,
    port: PORT_IMPLICIT,
    linkGroup: 'A',
    // Bursa pe care se tranzacționează. Formațiile se caută pe Binance (acolo e volumul),
    // dar ordinul pleacă de pe bursa contului — de obicei Bybit. „" = aceeași bursă pe
    // care s-a găsit moneda.
    tradeExchange: 'bybit',
    ...salvat,
    names,
  };
}

export function saveTiger(patch) {
  const next = { ...tigerState(), ...patch };
  settings.set(KEY, next);
  bus.emit('tiger:change', next);
  return next;
}

class Tiger {
  #ws = null;
  #asteapta = [];
  /** 'idle' | 'connecting' | 'open' | 'error' */
  status = 'idle';

  get enabled() {
    return tigerState().on;
  }

  #setStatus(status, detail = '') {
    this.status = status;
    bus.emit('tiger:status', { status, detail });
  }

  #connect() {
    if (this.#ws && (this.#ws.readyState === WebSocket.OPEN || this.#ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const { port } = tigerState();
    this.#setStatus('connecting');
    try {
      this.#ws = new WebSocket(`ws://127.0.0.1:${port}`);
    } catch (err) {
      // Pagina e pe HTTPS, iar `ws://` spre localhost e permis doar fiindcă `127.0.0.1`
      // e considerat de încredere. Dacă browserul refuză, spunem asta pe șleau.
      this.#setStatus('error', String(err?.message || err));
      return;
    }

    this.#ws.onopen = () => {
      this.#setStatus('open');
      const coada = this.#asteapta.splice(0);
      for (const msg of coada) {
        bus.emit('tiger:log', { dir: 'out', text: msg });
        this.#ws.send(msg);
      }
    };
    this.#ws.onerror = () => {
      this.#setStatus('error', 'Терминал не отвечает на 127.0.0.1');
      // Dacă utilizatorul tocmai a apăsat pe o monedă, îi spunem de ce nu s-a
      // întâmplat nimic — altfel butonul pare stricat.
      if (this.#asteapta.length) {
        this.#asteapta.length = 0;
        bus.emit('tiger:error',
          'Tiger.Trade не отвечает. Запустите терминал и включите '
          + 'Settings → Local Signal Server → Enable Signal Server.');
      }
    };
    this.#ws.onclose = () => {
      this.#ws = null;
      if (this.status !== 'error') this.#setStatus('idle');
    };
    this.#ws.onmessage = (ev) => {
      bus.emit('tiger:log', { dir: 'in', text: String(ev.data).slice(0, 400) });
      // Terminalul răspunde cu titlu și mesaj; erorile de validare nu închid socketul.
      try {
        const data = JSON.parse(ev.data);
        if (/error|invalid|not found|unknown/i.test(`${data?.title} ${data?.message}`)) {
          const cerere = data.request || {};
          // „Symbol not found" înseamnă aproape întotdeauna că bursa nu e conectată
          // în terminal sau că numele ei diferă — nu că moneda n-ar exista.
          const detaliu = /not found/i.test(data.title || '')
            ? `Tiger.Trade не знает «${cerere.e}» (${cerere.m}). `
              + 'Подключите эту биржу в терминале или исправьте её название в настройках.'
            : (data.message || data.title);
          bus.emit('tiger:error', detaliu);
        }
      } catch { /* „pong" simplu, nu e JSON */ }
    };
  }

  /**
   * Deschide perechea în terminal. Întoarce `false` dacă integrarea e oprită —
   * apelantul decide ce mesaj arată.
   */
  open(symbol, exchange, market) {
    const st = tigerState();
    if (!st.on) return false;

    const msg = JSON.stringify({
      type: 'setLinkSymbol',
      e: numeBursa(exchange, market),
      m: market === 'spot' ? 'SPOT' : 'FUTURES',
      symbol,
      linkGroup: st.linkGroup || 'A',
    });

    if (this.#ws?.readyState === WebSocket.OPEN) {
      bus.emit('tiger:log', { dir: 'out', text: msg });
      this.#ws.send(msg);
      return true;
    }
    // Socketul se deschide la prima cerere; mesajul pleacă imediat ce e gata.
    this.#asteapta.push(msg);
    if (this.#asteapta.length > 5) this.#asteapta.shift();
    this.#connect();
    return true;
  }

  /**
   * Verificare din setări: deschide socketul și trimite un „ping".
   *
   * O conexiune deschisă nu dovedește că terminalul înțelege comenzile — la fel ca la
   * bursele care acceptă socketul și nu livrează nimic. Ping-ul cere un răspuns.
   */
  test() {
    this.#connect();
    const trimite = () => {
      if (this.#ws?.readyState !== WebSocket.OPEN) return;
      const msg = JSON.stringify({ type: 'ping', title: 'test', message: 'test' });
      bus.emit('tiger:log', { dir: 'out', text: msg });
      this.#ws.send(msg);
    };
    if (this.#ws?.readyState === WebSocket.OPEN) trimite();
    else setTimeout(trimite, 700);
  }
}

export const tiger = new Tiger();
export const TIGER_EXCHANGES = NUME_IMPLICITE;
